export type LocationPart = string | number | null | undefined;

export interface FormatLocationOptions {
  /**
   * Separator preceding each part, positionally aligned with `parts`:
   * `separators[i]` is used before `parts[i + 1]`, even when earlier parts
   * were dropped as empty. The last separator repeats if fewer are supplied
   * than there are gaps. Defaults to `[', ']`.
   */
  separators?: readonly string[];

  /** Values that should be treated as absent location parts. */
  ignoredValues?: readonly string[];

  /**
   * Value returned when no valid location parts are available.
   * Non-finite numbers are treated as absent; callers are
   * responsible for filtering out numeric sentinel values like 0
   * that aren't meaningful in their domain (e.g., a zip code)."
   *  */
  emptyValue?: string;
}

export function formatLocation(
  parts: readonly LocationPart[] = [],
  options: FormatLocationOptions = {}
): string {
  const { separators: separatorsOption, ignoredValues = [], emptyValue = '' } = options;

  const separators = separatorsOption && separatorsOption.length > 0 ? separatorsOption : [', '];
  const ignored = new Set(ignoredValues.map((value) => value.trim()));

  const formattedParts = parts.map((part) => {
    if (part === null || part === undefined) return '';
    if (typeof part === 'number') return Number.isFinite(part) ? String(part) : '';
    return part.trim();
  });

  const validParts = formattedParts.filter((part) => part && !ignored.has(part));

  if (validParts.length === 0) {
    return emptyValue;
  }

  return formattedParts.reduce((result, part, index) => {
    if (!part || ignored.has(part)) {
      return result;
    }

    if (result.length === 0) {
      return part;
    }

    const separator = separators[index - 1] ?? separators[separators.length - 1];

    return `${result}${separator}${part}`;
  }, '');
}
